import { useEffect, useMemo, useState } from 'react';
import { UTCTimestamp } from 'lightweight-charts';
import { useLocalStorage } from 'usehooks-ts';
import { GoCheck } from 'react-icons/go';
import { TbDownload } from 'react-icons/tb';
import { IoIosSwap } from 'react-icons/io';
import { ChainId, NATIVE_ADDRESS, ZERO_ADDRESS } from '@shared/utils/config/networks';
import { getUrls } from '@shared/utils/config/environment';
import { useQuery } from '@tanstack/react-query';
import { TPnLData } from '@shared/types/frontend/assetPage';
import { TradeMode } from '@shared/types/frontend/trade';
import { CovalentBalanceItem } from '@shared/types/frontend';
import MobileActions from './MobileActions';
import MobileTopNav from '@/components/shared/TopNav/MobileTopNav';
import usePortfolioBalance from '@/hooks/portfolio/usePortfolioBalance';
import { resolutionData } from '@/lib/tradingview/chartData';
import PortfolioChart, { CurrentPrice } from '@/components/charts/PortfolioChart';
import { PortfolioHeader } from '@/components/assets/PortfolioHeader';
import MobileDeposit from '@/components/deposit/MobileDeposit';
import SlidingModal from '../SlidingModal';
import TokenToSend from '@/components/portfolio/desktop/TokenToSend';
import ImageWithFallback from '@/components/shared/ImageWithFallback';
import TotalPortfolioLink from './TotalPortfolioLink';
import { PortfolioAssetItem } from '@/components/assets/PortfolioAssetItem';
import MobileBuySellController from '@/components/asset/mobile/mobileBuySellAsset/MobileBuySellController';
import { Button } from '@/components/ui/button';
import SendFundsController from '@/components/asset/sendFunds/SendFundsController';
import { useMixpanel } from '@/lib/misc/mixpanel';
import { useSwap } from '@/contexts/SwapProvider';
import SwapMobileController from '@/components/trade/mobile/SwapMobileController';
import { useSession } from '@/contexts/LitSessionProvider';
import SwapSuccess from '@/components/trade/mobile/SwapSuccess';
import { stableCoins } from '@/components/explore/stableCoins';
import { TAllowedCurrencies } from '@/hooks/admin/useAppSettings';
import { SOLANA_NATIVE_ADDRESS, SOLANA_NATIVE_SIMPLE } from '@/lib/asset/nativeAddressHelper';
import useAppDetails from '@/hooks/utilities/useAppDetails';
export type TAssetModes = 'Balance' | 'Returns (24hr)' | 'Gains (24hr)' | 'Allocation';
export default function MobilePortfolio({
  className
}: {
  className?: string;
}) {
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  const {
    data: assetData
  } = usePortfolioBalance({});
  const {
    resetAssets
  } = useSwap();
  const {
    PKP
  } = useSession();
  const [currentPrice, setCurrentPrice] = useLocalStorage<CurrentPrice>('currentPrice', null);
  const [currentResolution, setCurrentResolution] = useState(resolutionData[0]);
  const [showCashModal, setShowCashModal] = useState(false);
  const [networksModalData, setNetworksModalData] = useState<any[] | null>(null);
  const [selectedToken, setSelectedToken] = useState<CovalentBalanceItem | null>(null);
  const [isFlexGainsOpen, setFlexGainsOpen] = useState(false);
  const [isDepositModalOpen, setDepositModalOpen] = useState(false);
  const [isSendModalOpen, setSendModalOpen] = useState(false);
  const [isSwapModalOpen, setSwapModalOpen] = useState(false);
  const [tradeMode, setTradeMode] = useState<TradeMode>(TradeMode.SWAP);
  const {
    setDepositCurrencyOverride
  } = useAppDetails();
  const [selectedNetwork, setSelectedNetwork] = useState({
    name: 'All Networks',
    value: `$${assetData?.totalUsdValue?.toLocaleString()}`,
    logo: '/static/genius.png'
  });
  const [assetMode, setAssetMode] = useLocalStorage<TAssetModes>('genius-assets-portfolio-mode', 'Balance');
  useEffect(() => {
    setCurrentPrice({
      value: assetData?.totalUsdValue || 0,
      before: assetData?.totalUsdValue24h || 0,
      time: Date.now() as UTCTimestamp
    });
  }, [assetData]);
  const goToNextAssetMode = () => {
    if (assetMode === 'Balance') {
      setAssetMode('Returns (24hr)');
    } else if (assetMode === 'Returns (24hr)') {
      setAssetMode('Gains (24hr)');
    } else if (assetMode === 'Gains (24hr)') {
      setAssetMode('Allocation');
    } else if (assetMode === 'Allocation') {
      setAssetMode('Balance');
    }
  };
  const baseFilteredBalances = useMemo(() => (selectedNetwork.name === 'All Networks' ? assetData?.balances : assetData?.balancesByChain?.find(chain => chain.networkName === selectedNetwork.name)?.balances || [])?.filter((x: any) => x?.contract_address).filter(x => x.usd_value > 0).sort((a: any, b: any) => b.usd_value - a.usd_value), [selectedNetwork, assetData]);
  const [filteredBalances, setFilteredBalances] = useState<CovalentBalanceItem[]>(baseFilteredBalances);
  useEffect(() => {
    if (showCashModal) {
      const cashBalances = baseFilteredBalances.filter(asset => asset.contract_ticker_symbol === 'USDC' || asset.contract_ticker_symbol === 'USDT');
      setFilteredBalances(cashBalances);
    } else {
      setFilteredBalances(baseFilteredBalances);
    }
  }, [baseFilteredBalances, showCashModal]);
  const handleChangeChart = (value: number, beforeValue: number, timestamp: UTCTimestamp) => {
    setCurrentPrice({
      value: value,
      before: beforeValue,
      time: timestamp
    });
  };
  const {
    data: tokenPnLData
  } = useQuery({
    queryKey: ['pnlData', selectedToken?.contract_address, selectedToken?.network],
    queryFn: async (): Promise<TPnLData | undefined> => {
      const data = await fetch(`${getUrls().INDEXER_URL}/portfolio/holdings?address=${selectedToken?.network == ChainId.SOLANA ? PKP?.solanaAddress : PKP?.additionalEthAddress}&token=${selectedToken?.contract_address}&chains=${selectedToken?.network}`).then(res => res.json());
      const pnls = data?.pnls?.[0] ?? null;
      return pnls;
    },
    refetchInterval: 5000,
    enabled: Boolean(selectedToken && selectedToken?.contract_address && selectedToken?.network && PKP)
  });
  const depositAvailable = () => {
    const usdcStableCoinAddresses = Object.values(stableCoins.find(coin => coin.name === 'usdc')?.tokens).map(x => x.toLowerCase());
    //bsc only
    const usdtStableCoinAddresses = ['0x55d398326f99059ff775485246999027b3197955'];
    const nativeAddresses = [NATIVE_ADDRESS, ZERO_ADDRESS, SOLANA_NATIVE_ADDRESS, SOLANA_NATIVE_SIMPLE].map(x => x.toLowerCase());
    if (nativeAddresses.includes(selectedToken?.contract_address?.toLowerCase()) || usdcStableCoinAddresses.includes(selectedToken?.contract_address?.toLowerCase()) || usdtStableCoinAddresses.includes(selectedToken?.contract_address?.toLowerCase())) {
      let assetName: TAllowedCurrencies;
      const isNative = nativeAddresses.includes(selectedToken?.contract_address?.toLowerCase());
      switch (selectedToken?.network) {
        case ChainId.ETHEREUM:
          assetName = isNative ? `ETH_ETHEREUM` : `USDC_ETHEREUM`;
          break;
        case ChainId.SOLANA:
          assetName = isNative ? `SOL` : `USDC_SOL`;
          break;
        case ChainId.POLYGON:
          assetName = isNative ? `POL` : `USDC_POLYGON`;
          break;
        case ChainId.AVALANCHE:
          assetName = isNative ? `AVAX` : `USDC_AVAX`;
          break;
        case ChainId.BSC:
          assetName = isNative ? `BNB` : `USDT_BNB`;
          break;
        case ChainId.SUI:
          assetName = isNative ? `SUI` : `USDC_SUI`;
          break;
        case ChainId.APTOS:
          assetName = isNative ? `APTOS` : `USDC_APTOS`;
          break;
        case ChainId.BASE:
          assetName = isNative ? `ETH_BASE` : `USDC_BASE`;
          break;
        case ChainId.ARBITRUM:
          assetName = isNative ? `ETH_ARBITRUM` : `USDC_ARBITRUM`;
          break;
        case ChainId.OPTIMISM:
          assetName = isNative ? `ETH_OPTIMISM` : `USDC_OPTIMISM`;
          break;
        default:
          assetName = `SOL`;
          break;
      }
      return {
        available: true,
        asset: assetName
      };
    } else {
      return {
        available: false,
        asset: null
      };
    }
  };

  // function createVirtualElements(): MobilePortfolioListItem[] {
  //   const elements: MobilePortfolioListItem[] = [];

  //   // Total Portfolio
  //   elements.push({
  //     type: MOBILE_PORTFOLIO_ITEM_TYPES.TOTAL_PORTFOLIO,
  //     height: 72,
  //     data: {
  //       assetData,
  //       currentPrice,
  //       currentResolution,
  //     },
  //   });

  //   // Actions
  //   elements.push({
  //     type: MOBILE_PORTFOLIO_ITEM_TYPES.ACTIONS,
  //     height: 81,
  //     data: {
  //       setSendModalOpen: setIsSendModalOpen,
  //       setDepositModalOpen: setIsDepositModalOpen,
  //     },
  //   });

  //   // Chart
  //   elements.push({
  //     type: MOBILE_PORTFOLIO_ITEM_TYPES.CHART,
  //     height: 336,
  //     data: {
  //       onValueChange: handleChangeChart,
  //       setCurrentPrice,
  //       currentResolution,
  //       setCurrentResolution,
  //       assetData,
  //     },
  //   });

  //   // Portfolio header
  //   elements.push({
  //     type: MOBILE_PORTFOLIO_ITEM_TYPES.PORTFOLIO_HEADER,
  //     height: 152,
  //     data: {
  //       showCashModal,
  //       setShowCashModal,
  //       assetMode,
  //       goToNextAssetMode,
  //       assetData,
  //       selectedNetwork,
  //       setNetworksModalData,
  //     },
  //   });

  //   // Asset rows
  //   filteredBalances.forEach((balance) => {
  //     elements.push({
  //       type: MOBILE_PORTFOLIO_ITEM_TYPES.ASSET_ROW,
  //       height: 70,
  //       data: {
  //         assetMode,
  //         assetItem: balance,
  //         assetData,
  //         setAssetActionsModalData,
  //       },
  //     });
  //   });

  //   return elements;
  // }

  // const elements = createVirtualElements();
  // console.log('depositCurrencyOverride', depositCurrencyOverride);

  return <div className={`h-full bg-genius-indigo ${className} overflow-y-auto pb-28`} data-sentry-component="MobilePortfolio" data-sentry-source-file="MobilePortfolio.tsx">
      <MobileTopNav className='static' data-sentry-element="MobileTopNav" data-sentry-source-file="MobilePortfolio.tsx" />
      <TotalPortfolioLink assetData={assetData} currentPrice={currentPrice} currentResolution={currentResolution} data-sentry-element="TotalPortfolioLink" data-sentry-source-file="MobilePortfolio.tsx" />
      <MobileActions setSendModalOpen={setSendModalOpen} setDepositModalOpen={setDepositModalOpen} setSwapModalOpen={setSwapModalOpen} data-sentry-element="MobileActions" data-sentry-source-file="MobilePortfolio.tsx" />
      <div className='homepage-chart'>
        <PortfolioChart onValueChange={handleChangeChart} setCurrentPrice={setCurrentPrice} currentResolution={currentResolution} setCurrentResolution={setCurrentResolution} assetData={assetData} data-sentry-element="PortfolioChart" data-sentry-source-file="MobilePortfolio.tsx" />
      </div>
      <div>
        <PortfolioHeader showCashModal={showCashModal} setShowCashModal={setShowCashModal} assetMode={assetMode} goToNextAssetMode={goToNextAssetMode} assetData={assetData} selectedNetwork={selectedNetwork} setNetworksModalData={setNetworksModalData} data-sentry-element="PortfolioHeader" data-sentry-source-file="MobilePortfolio.tsx" />
      </div>
      {filteredBalances?.map((balance: CovalentBalanceItem, index: number) => {
      return <PortfolioAssetItem key={index} assetMode={assetMode} assetItem={balance} assetData={assetData} setSelectedToken={setSelectedToken} />;
    })}

      {/* <PortfolioAssetItem 
        assetMode={assetMode}
        assetItem={filteredBalances[0]}
        assetData={assetData}
        setAssetActionsModalData={setAssetActionsModalData}
       /> */}
      {/* <AutoSizer>
        {({ height, width }) => (
          <List
            height={height}
            width={width}
            itemCount={elements.length}
            itemSize={(index: number) => elements[index].height}
            className='no-scrollbar'
            style={{ paddingBottom: 140, overflowX: 'hidden' }}
          >
            {(props) => {
              const { style, index } = props;
              const element = elements[index];
               switch (element.type) {
                // case MOBILE_PORTFOLIO_ITEM_TYPES.TOTAL_PORTFOLIO:
                //   return (
                //     <div style={style}>
                //       <TotalPortfolioLink {...element.data} />
                //     </div>
                //   );
                // case MOBILE_PORTFOLIO_ITEM_TYPES.ACTIONS:
                //   return (
                //     <div style={style}>
                //       <MobileActions {...element.data} />
                //     </div>
                //   );
                // case MOBILE_PORTFOLIO_ITEM_TYPES.CHART:
                //   return (
                //     <div style={style} className='homepage-chart pointer-events-none'>
                //       <PortfolioChart {...element.data} />
                //     </div>
                //   );
                // case MOBILE_PORTFOLIO_ITEM_TYPES.PORTFOLIO_HEADER:
                //   return (
                //     <div style={style}>
                //       <PortfolioHeader {...element.data} />
                //     </div>
                //   );
                case MOBILE_PORTFOLIO_ITEM_TYPES.ASSET_ROW:
                  return (
                    <div style={style}>
                      <PortfolioAssetItem {...element.data} />
                    </div>
                  );
                default:
                  return null;
              }
            }}
          </List>
        )}
       </AutoSizer> */}

      {/* MODALS */}

      <SlidingModal heightFit isOpen={networksModalData !== null} onClose={() => setNetworksModalData(null)} data-sentry-element="SlidingModal" data-sentry-source-file="MobilePortfolio.tsx">
        <div className='pt-4'>
          <ul className='w-full'>
            {networksModalData?.map(network => <li key={network.networkName || 'all-networks'} className='px-4 cursor-pointer md:hover:bg-genius-blue flex justify-between items-center py-3 border-genius-blue' onClick={() => {
            setSelectedNetwork({
              name: network.networkName || 'All Networks',
              value: `$${network?.totalUsdValue?.toFixed(2)}`,
              logo: network.networkLogo
            });
            setNetworksModalData(null);
          }}>
                <div className='flex items-center w-3/5'>
                  {network.networkLogo && <ImageWithFallback src={network.networkLogo} width={20} height={20} alt={`${network.networkName} logo`} className='mr-4 w-[20px] h-[20px]' />}
                  <span className='uppercase text-xs'>
                    {network.networkName === 'eth-mainnet' ? 'Ethereum' : network.networkName.replace('-mainnet', '')}
                  </span>
                  {selectedNetwork.name === network.networkName && <GoCheck className='h-4 w-4 ml-4' />}
                </div>
                <div className='flex items-end w-2/5 text-xs'>
                  <div className='flex-col w-3/4 ta-r pr-1'>
                    ${network?.totalUsdValue?.toFixed(2)}
                  </div>
                  <div className='flex-col text-genius-dark'>
                    ({network.totalUsdRate?.toFixed(2)}%)
                  </div>
                </div>
              </li>)}
          </ul>
        </div>
      </SlidingModal>

      <SlidingModal heightFit isOpen={selectedToken !== null} onClose={() => setSelectedToken(null)} data-sentry-element="SlidingModal" data-sentry-source-file="MobilePortfolio.tsx">
        <div className='flex flex-col gap-2 px-4 pb-6'>
          <div className='flex items-center'>
            <MobileBuySellController setTradeMode={setTradeMode} setSwapModalOpen={setSwapModalOpen} asset={selectedToken} balance={selectedToken?.balance} data-sentry-element="MobileBuySellController" data-sentry-source-file="MobilePortfolio.tsx" />
          </div>
          {!selectedToken?.contract_ticker_symbol?.includes('USDC') && !selectedToken?.contract_ticker_symbol?.includes('USDT') && <Button className='flex items-center gap-2 h-12 rounded-xl bg-genius-blue text-genius-cream' variant='purple' onClick={() => {
          LogToMixpanel({
            pkp: PKP,
            eventName: EVENTS.portfolio.startTrade,
            category: CATEGORIES.portfolio,
            metaData: {
              tokenAddress: selectedToken?.contract_address,
              networkId: selectedToken?.network
            }
          });
          resetAssets();
          setTradeMode(TradeMode.SWAP);
          setSwapModalOpen(true);
        }}>
                Swap
                <IoIosSwap className='text-lg' />
              </Button>}
          {!selectedToken?.contract_ticker_symbol?.includes('USDC') && !selectedToken?.contract_ticker_symbol?.includes('USDT') && <>
                <Button className='flex items-center h-12 rounded-xl bg-genius-blue text-genius-cream' variant='purple' onClick={() => setFlexGainsOpen(true)}>
                  Flex Your Gains 🎉
                </Button>
              </>}

          <SendFundsController tokenData={{
          address: selectedToken?.contract_address,
          network: selectedToken?.network
        }} data-sentry-element="SendFundsController" data-sentry-source-file="MobilePortfolio.tsx" />

          {depositAvailable()?.available && <>
              <Button className='flex items-center gap-2 h-12 rounded-xl bg-genius-blue text-genius-cream' variant='purple' onClick={() => {
            setDepositModalOpen(true);
            setDepositCurrencyOverride(depositAvailable()?.asset);
          }}>
                Deposit
                <TbDownload className='text-lg' />
              </Button>
            </>}
        </div>
      </SlidingModal>

      <SlidingModal heightFit isOpen={isFlexGainsOpen} onClose={() => setFlexGainsOpen(false)} data-sentry-element="SlidingModal" data-sentry-source-file="MobilePortfolio.tsx">
        <SwapSuccess tradeMode={TradeMode.SELL} asset={selectedToken} closeModal={() => setFlexGainsOpen(false)} pnLData={tokenPnLData} data-sentry-element="SwapSuccess" data-sentry-source-file="MobilePortfolio.tsx" />
      </SlidingModal>

      <SlidingModal isOpen={isSendModalOpen} onClose={() => setSendModalOpen(false)} data-sentry-element="SlidingModal" data-sentry-source-file="MobilePortfolio.tsx">
        <TokenToSend data-sentry-element="TokenToSend" data-sentry-source-file="MobilePortfolio.tsx" />
      </SlidingModal>

      <MobileDeposit isModalOpen={isDepositModalOpen} setIsModalOpen={setDepositModalOpen} data-sentry-element="MobileDeposit" data-sentry-source-file="MobilePortfolio.tsx" />

      <SwapMobileController isOpen={isSwapModalOpen} setTradeMode={setTradeMode} onClose={() => setSwapModalOpen(false)} tradeMode={tradeMode} tradedAsset={selectedToken} portfolioBalance={assetData} fromPortfolio={true} data-sentry-element="SwapMobileController" data-sentry-source-file="MobilePortfolio.tsx" />
    </div>;
}